import React, { useState, useEffect } from 'react';
import { configService } from '../services/configService';
import FeatureEngagementModal from './FeatureEngagementModal';

function SubscriptionManager() {
  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [currentProduct, setCurrentProduct] = useState(null);
  const [engagementModal, setEngagementModal] = useState({ isOpen: false, plan: null });

  useEffect(() => {
    loadData();
  }, []);

  // Debug function for browser console
  window.debugSubscriptionData = () => {
    console.log('=== SUBSCRIPTION DEBUG ===');
    console.log('Subscription Status:', subscriptionStatus);
    console.log('Available fields in status:', Object.keys(subscriptionStatus || {}));
    console.log('Current Product:', currentProduct);
    console.log('Available Plans:', plans);
    console.log('Current ValueOrder:', currentProduct?.metadata?.ValueOrder);
    console.log('Filtered Plans:', getFilteredPlans());
    console.log('=== MATCHING LOGIC ===');
    console.log('Status plan_id:', subscriptionStatus?.plan_id);
    console.log('Status price_id:', subscriptionStatus?.price_id);
    console.log('Product match attempts:');
    plans.forEach(p => {
      console.log(`  ${p.name} (${p.price_id}): matches plan_id=${p.name === subscriptionStatus?.plan_id}, matches price_id=${p.price_id === subscriptionStatus?.price_id}`);
    });
  };

  const loadData = async () => {
    try {
      const [statusResponse, plansResponse] = await Promise.all([
        configService.getSubscriptionStatus(),
        configService.getSubscriptionProducts()
      ]);
      
      setSubscriptionStatus(statusResponse);
      
      // Find current product details from plans
      let current = null;
      if (statusResponse?.stripe_subscription_id) {
        // Try to match by price_id first (most reliable)
        if (statusResponse.price_id) {
          current = plansResponse.products.find(p => p.price_id === statusResponse.price_id);
        }
        // Fallback: try to match by plan_id
        if (!current && statusResponse.plan_id && statusResponse.plan_id !== 'Unknown Plan') {
          current = plansResponse.products.find(p => 
            p.name === statusResponse.plan_id || p.price_id === statusResponse.plan_id
          );
        }
        // If still no match but user has active subscription, assume lowest tier for filtering
        if (!current && (statusResponse.status === 'active' || statusResponse.status === 'trialing')) {
          current = plansResponse.products.find(p => p.metadata?.ValueOrder === '1');
        }
      }
      setCurrentProduct(current);
      
      setPlans(plansResponse.products);
    } catch (error) {
      setError('Failed to load subscription data');
      console.error('Error loading subscription data:', error);
    } finally {
      setLoading(false);
    }
  };

  const getFilteredPlans = () => {
    if (!currentProduct?.metadata?.ValueOrder) {
      return plans.filter(p => p.interval === 'month' || p.interval === 'year');
    }
    
    const currentValueOrder = parseInt(currentProduct.metadata.ValueOrder) || 0;
    return plans.filter(p => {
      const planValueOrder = parseInt(p.metadata?.ValueOrder) || 0;
      return planValueOrder > currentValueOrder && (p.interval === 'month' || p.interval === 'year');
    });
  };

  const getSubscriptionDisplayInfo = () => {
    if (!subscriptionStatus) return null;
    
    const isTrialing = subscriptionStatus.status === 'trialing';
    const isAnnual = currentProduct?.interval === 'year';
    
    let statusText = subscriptionStatus.status === 'active' ? '✅ Active' : 
                    isTrialing ? '🔄 Free Trial' : '❌ Inactive';
    
    let planText = currentProduct?.name || subscriptionStatus.plan_id || 'Unknown Plan';
    if (isTrialing && isAnnual) {
      planText += ' (14-day trial for annual plan)';
    } else if (isTrialing) {
      planText += ' (14-day trial)';
    }
    
    return { statusText, planText };
  };

  const handlePlanChange = async (newPlan) => {
    // Check if this is a waitlist product
    if (newPlan.metadata?.WaitList === 'True') {
      setEngagementModal({
        isOpen: true,
        plan: newPlan
      });
      return;
    }

    try {
      setLoading(true);
      const checkoutData = {
        price_id: newPlan.price_id,
        success_url: `${window.location.origin}/dashboard?upgrade=success`,
        cancel_url: `${window.location.origin}/dashboard?upgrade=cancelled`
      };
      
      const response = await configService.createCheckoutSession(checkoutData);
      window.location.href = response.checkout_url;
    } catch (error) {
      setError('Failed to change plan');
      console.error('Plan change error:', error);
      setLoading(false);
    }
  };

  const closeEngagementModal = () => {
    setEngagementModal({ isOpen: false, plan: null });
  };

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '2rem' }}>
        <div>Loading subscription details...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ 
        background: '#fee2e2', 
        color: '#dc2626', 
        padding: '1rem', 
        borderRadius: '8px',
        textAlign: 'center'
      }}>
        {error}
      </div>
    );
  }

  const displayInfo = getSubscriptionDisplayInfo();
  const filteredPlans = getFilteredPlans();

  return (
    <div style={{
      background: 'white',
      padding: '2rem',
      borderRadius: '12px',
      boxShadow: '0 4px 6px rgba(0,0,0,0.05)'
    }}>
      <h3 style={{ marginBottom: '1.5rem' }}>Subscription Management</h3>
      
      {subscriptionStatus && displayInfo && (
        <div style={{
          background: '#f8fafc',
          padding: '1rem',
          borderRadius: '8px',
          marginBottom: '2rem'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '0.5rem' }}>
            Current Status: {displayInfo.statusText}
          </div>
          <div>Plan: {displayInfo.planText}</div>
          {subscriptionStatus.current_period_end && (
            <div style={{ fontSize: '0.9rem', color: '#64748b' }}>
              {subscriptionStatus.status === 'trialing' ? 'Trial ends' : 'Next billing'}: {' '}
              {new Date(subscriptionStatus.current_period_end).toLocaleDateString()}
            </div>
          )}
        </div>
      )}

      <h4 style={{ marginBottom: '1rem' }}>Available Plans</h4>
      {filteredPlans.length === 0 ? (
        <div style={{ 
          textAlign: 'center', 
          padding: '2rem', 
          color: '#64748b',
          fontStyle: 'italic'
        }}>
          No upgrade options available
        </div>
      ) : (
        <div style={{ display: 'grid', gap: '1rem' }}>
          {filteredPlans.map((plan) => {
            const isCurrentPlan = subscriptionStatus?.plan_id === plan.name || 
                                subscriptionStatus?.plan_id === plan.price_id;
            return (
              <div key={plan.price_id} style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <div style={{ fontWeight: 'bold' }}>{plan.name}</div>
                  <div style={{ color: '#64748b' }}>
                    ${(plan.amount / 100).toFixed(0)}/{plan.interval === 'month' ? 'month' : 'year'}
                  </div>
                </div>
                <button
                  className={`px-6 py-3 rounded-xl font-semibold shadow transition-colors ${
                    isCurrentPlan
                      ? 'bg-gray-200 text-gray-400 cursor-not-allowed' 
                      : 'bg-primary hover:bg-primary-hover text-white cursor-pointer'
                  }`}
                  onClick={() => handlePlanChange(plan)}
                  disabled={isCurrentPlan}
                >
                  {isCurrentPlan ? 'Current Plan' : 'Upgrade'}
                </button>
              </div>
            );
          })}
        </div>
      )}
      
      <FeatureEngagementModal
        isOpen={engagementModal.isOpen}
        onClose={closeEngagementModal}
        featureType="subscription_plan"
        featureId={engagementModal.plan?.id}
        featureName={engagementModal.plan?.name}
        context={{
          page: 'dashboard',
          location: 'subscription_manager',
          price_id: engagementModal.plan?.price_id
        }}
      />
    </div>
  );
}

export default SubscriptionManager;